<template>
  <div class="menu-editor" :class="{ collapsed: isCollapsed }">

    <!-- ===== HEADER BAR ===== -->
    <div class="panel-header">
      <div class="left">
        <i class="bx bx-menu-alt-left"></i>
        <span v-if="!isCollapsed" class="title-text">Navigation Menu</span>
      </div>
      
      <button class="collapse-btn" @click="toggleCollapse">
        <i :class="isCollapsed ? 'bx bx-chevrons-right' : 'bx bx-chevrons-left'"></i>
      </button>
    </div>

    <p v-if="!isCollapsed" class="panel-sub">
      Manage navigation labels, links and visibility
    </p>

    <!-- ===== MENU LIST ===== -->
    <transition-group name="fade-list" tag="div" class="menu-list">
      <div v-for="(item, i) in menu" :key="i" class="menu-card">

        <!-- Drag Icon (future sorting support if needed) -->
        <span class="sort">
          <i class="bx bx-grid-small"></i>
        </span>

        <!-- Label Input -->
        <div class="flex-1" v-if="!isCollapsed">
          <input 
            class="menu-input"
            v-model="item.label"
            placeholder="Menu name"
            @input="applyUpdate"
          />

          <!-- Link Input -->
          <input
            class="menu-input link-input"
            v-model="item.href"
            placeholder="URL / #"
            @input="applyUpdate"
          />
        </div>

        <!-- Actions -->
        <div class="actions">
          <button 
            class="icon-btn" 
            :class="{ active: item.hidden }" 
            @click="toggle(i)">
            <i :class="item.hidden ? 'bx bx-low-vision' : 'bx bx-show'"></i>
          </button>

          <button class="icon-btn danger" @click="remove(i)">
            <i class="bx bx-trash"></i>
          </button>
        </div>
      </div>
    </transition-group>

    <!-- Add New -->
    <button v-if="!isCollapsed" class="add-btn" @click="add">
      <i class="bx bx-plus"></i> Add Menu Item
    </button>

  </div>
</template>

<script>
export default {
  name: "MenuEditor",

  props: {
    navItems: { type: Array, default: () => [] }
  },

  emits: ["updateMenu"],

  data() {
    return {
      menu: [],
      isCollapsed: false
    };
  },

  watch: {
    navItems: {
      immediate: true,
      handler(v) {
        this.menu = JSON.parse(JSON.stringify(v || []));
      }
    }
  },

  methods: {
    /** Called by parent after clicking menu element */
    loadMenu(items) {
      this.menu = JSON.parse(JSON.stringify(items || []));
    },

    toggleCollapse() { this.isCollapsed = !this.isCollapsed; },

    add() {
      this.menu.push({ label: "New Menu", href: "#", hidden: false });
      this.applyUpdate();
    },

    remove(i) {
      this.menu.splice(i, 1);
      this.applyUpdate();
    },

    toggle(i) {
      this.menu[i].hidden = !this.menu[i].hidden;
      this.applyUpdate();
    },

    applyUpdate() {
      this.$emit("updateMenu", JSON.parse(JSON.stringify(this.menu)));
    }
  }
};
</script>
<style scoped>
/* WRAPPER */
.menu-editor {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  border-radius: 12px;
  padding: 14px;
  transition: all .28s ease;
  background: var(--card);
  border: 1px solid var(--border);
}
.menu-editor.collapsed {
  width: 72px !important;
  padding: 14px 10px;
}

/* HEADER */
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.panel-header .left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.title-text {
  font-weight: 600;
  font-size: 13.5px;
}
.panel-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: -6px;
  margin-bottom: 4px;
  line-height: 1.4;
}

/* COLLAPSE BUTTON */
.collapse-btn {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: .25s ease;
}
.collapse-btn:hover {
  background: var(--primaryLite);
  border-color: var(--primary);
  color: var(--primary);
}

/* LIST */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.menu-card {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  transition: all .22s ease;
}
.menu-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--cardShadow);
}

/* COLLAPSE STATE */
.menu-editor.collapsed .menu-card {
  justify-content: center !important;
  padding: 12px;
}

/* SORT HANDLE */
.sort {
  color: var(--muted);
  font-size: 18px;
  cursor: grab;
}

/* INPUTS */
.flex-1 {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.menu-input {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 13px;
  transition: .22s;
  outline: none;
}
.menu-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(37,99,235,.15);
}
.link-input {
  font-size: 12px;
  margin-top: 3px;
}

/* ACTION BUTTONS */
.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.icon-btn {
  padding: 6px;
  border: none;
  border-radius: 8px;
  background: #f7f7f7;
  cursor: pointer;
  font-size: 16px;
  transition: .2s ease;
}
.icon-btn.active {
  background: #fff7dd;
  color: #d97706;
}
.icon-btn:hover {
  background: var(--primaryLite);
  color: var(--primary);
}
.icon-btn.danger:hover {
  background: #ffe5e5;
  color: #e11d48;
}

/* ADD BTN */
.add-btn {
  background: var(--primary);
  color: #fff;
  border-radius: 8px;
  padding: 10px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: .25s;
}
.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--buttonShadow);
}

/* TRANSITIONS */
.fade-list-enter-active, .fade-list-leave-active {
  transition: all .25s ease;
}
.fade-list-enter-from, .fade-list-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

/* Prevent card overflow */
.menu-card {
  overflow: hidden;
  min-height: 44px;
}

/* Stop text from pushing icons outside */
.flex-1 {
  min-width: 0;
}
</style>

